/**
 * 产品信息 → 提示词：**唯一一份组装逻辑**。
 *
 * ===========================================================================
 * 这一层是「一次一个产品」扩到「一次一批产品」时**唯一真正要改的地方**。
 * ===========================================================================
 *
 * 上一版 `header` / `prompts` / `prompt` 三个 `useMemo` 都直接从表单里读
 * `form.productName`、`form.sellingPoints`、`form.refs.length` —— 于是「这一批
 * 要出什么」和「这批产品是哪几个」被焊死在同一个对象上，一张表要做 20 个产品
 * 就无从下手（要么复制 20 份页面逻辑，要么在 `form` 上开个数组字段，两条路都会
 * 长出第二份真源）。
 *
 * 拆法：把**与产品无关**的部分收进 `PromptCtx`（平台规格、逐屏解构、模式、出图
 * 参数），把**与产品有关**的部分收进 `ProductInfo`，两者一相与就是提示词。
 * 单个模式传表单里填的那一项，批量模式传 CSV 的某一行 —— 同一套函数，没有分支。
 *
 * 所以批量**不是**新决策域：屏数、顺序、模块尺寸、叙事线全部由平台规格与逐屏
 * 解构决定（`planDetailShots` 的入参里根本没有产品信息），20 个产品共用同一套
 * 逐屏解构。变的只是「产品信息从哪来」。
 *
 * ===========================================================================
 * 为什么从 `DetailPageStudio.tsx` 提到 `lib/`（2026-09-29）
 * ===========================================================================
 *
 * 批量从「详情页里的一个输入形态」提升为**独立工作台**（`pages/BatchStudio.tsx`）
 * 之后，两个页面都要拼提示词。留在页面文件里就只有复制一份这条路 ——
 * 而提示词是**逐字有断言守着**的东西（`smoke-prompts`），复制出来的第二份
 * 改漏一处不会报错，只会让两条链路产出不一样的词。所以上提。
 */
import { ONE_SHOT_RULE, buildDetailPrompts, refFidelityBlock } from '@/config/prompts'
import { BEAT_NAME, type PlannedShot } from '@/config/detailPlan'
import { ratioHint, sizeLabel, type DetailSpec } from '@/config/platformDetails'
import {
  bannedBlock,
  sellingPointBlock,
  textLanguageRule,
  type PlatformProfile,
} from '@/config/platforms'
import type { StudioModeId } from '@/config/studioModes'
import type { Shot } from '@/config/studioShape'

/** 一个产品的两项信息 —— 单个模式来自表单，批量模式来自 CSV 的一行 */
export type ProductInfo = {
  productName: string
  sellingPoints: string
}

/**
 * 提示词组装的上下文 —— 与产品无关的那部分，全批次只有一份。
 *
 * ⚠️ 2026-09-30：「商品套图」下架后，`imageText`（卖点图带不带文字）与
 * `category` / `scene` 三个字段一起消失 —— 它们只被 `buildSetInput` 读过，
 * 而那条路径已整块删除（用户原话「这套图跟详情页重复了」）。
 * 留在类型里就是「一个不会改变任何输出的开关」。
 */
export type PromptCtx = {
  profile: PlatformProfile
  detail: DetailSpec
  mode: StudioModeId
  /** 形态显示名（「平台详情模块」按平台叫法，Amazon 就是「Amazon A+」） */
  modeName: string
  ratio: string
  resolution: string
  outputs: number
  /** 主图路径的逐张解构 */
  shots: (Shot | PlannedShot)[]
  /** 详情页路径的逐屏解构 */
  plannedShots: PlannedShot[]
  /**
   * **参考版式图的张数**（只有「模仿版式」这一档可能 > 0）。
   *
   * 2026-10-07 用户报障「这个模仿的，应该是上传长图或者多图，然后出风格、布局结构」。
   * 它是**批次级**的量（参考版式图整批共用一份，见 `/batch` 与详情页的
   * `RefUploader`），所以放上下文、不放 `ProductInfo` —— 放 `ProductInfo` 就成了
   * 「每行产品各带一张版式图」，与界面上只有一个上传口的现实相反。
   *
   * 它决定两件事，两件都会写进提示词：
   *   · 本批 N 张分别沿用第几张参考版式（`copyPlanFor` 的取模循环）；
   *   · 图片分工说明（前 k 张是产品图、之后 L 张是版式图）——
   *     不声明的话，模型会把版式图里那个**别人的产品**也当成本次的产品来还原。
   */
  layoutRefCount: number
  /**
   * 用户指定的**整批统一尺寸**（空 = 官方尺寸逐张匹配）。
   * 头部要据此覆盖那句「各模块尺寸不同，逐张按官方规格出」——
   * 不一致的话，提示词会让模型按官方比例画，而成图是按用户尺寸出的。
   */
  batchSize?: string
}

/**
 * 提交给上游的参考图顺序 —— 产品图在前、参考版式图在后。
 *
 * **顺序是契约，不是排版**：提示词里那句「第 1–k 张 = 产品图，
 * 第 k+1–k+L 张 = 参考版式图」是照着它写的（`buildCopyPrompts`）。
 * 两处各写一遍 `[...a, ...b]` 的话，哪天有人调了个顺序，
 * 提示词就会指着版式图说「这是你的产品」—— 而出图不会报错，
 * 只会把参考图里的**别人的产品**当成用户的货来还原。
 */
export function refOrder(productRefs: string[], layoutRefs: string[]): string[] {
  return [...productRefs, ...layoutRefs]
}

/** 模仿版式的一张产出：用第几张版式、是第几轮用它 */
export type CopySlot = {
  /** 本张序号（1 起） */
  no: number
  /** 沿用的参考版式图序号（1 起） */
  layoutNo: number
  /** 第几轮用到这张版式（> 1 = 参考版式少于张数、循环复用） */
  round: number
}

/**
 * 模仿版式的逐张分配 —— **张数与参考版式张数的关系只在这里定义一次**。
 *
 * 规则：第 i 张用第 `(i-1) % L + 1` 张版式。两条推论都要在提示词里说出来
 * （模型看不见这张表，只能看字）：
 *   · `N > L` → 有几张是**第 2 轮**复用同一张版式，必须换角度/场景/文案实例；
 *   · `L = 1 且 N > 1` → 那张参考图被当成**长图**，第 i 张对应它自上而下第 i 段。
 *
 * ⚠️ 页面上的「本批出图结构」卡与提示词层**共用这一个函数** ——
 * 各算一遍的话，卡片说「用第 3 张版式」而提示词让模型用第 1 张，
 * 两边都不报错（见铁律：能派生的别写两份）。
 *
 * `layoutCount = 0`（用户还没上传 / 从草稿恢复时丢了）按下标 1 兜底，
 * 由调用方去写「本次没有参考版式图」那句告警 —— 这里不替它编一张不存在的图。
 */
export function copyPlanFor(outputs: number, layoutCount: number): CopySlot[] {
  const total = Math.max(1, Math.floor(outputs) || 1)
  const layouts = Math.max(1, Math.floor(layoutCount) || 1)
  return Array.from({ length: total }, (_, i) => ({
    no: i + 1,
    layoutNo: (i % layouts) + 1,
    round: Math.floor(i / layouts) + 1,
  }))
}

/**
 * 模仿版式的「版式模仿要求」块 —— **一份实现，逐张提示词与总提示词共用**。
 *
 * 为什么不复用 `ONE_SHOT_RULE`：那条写的是「一个产品完整占据画面，严禁拼贴、
 * 严禁分格」—— 它治的是**主图**（一次要 N 张时模型爱把 N 种结构画进同一张）。
 * 模仿版式恰恰相反：参考版式**本身就是多模块的排版**（长图详情页就是典型），
 * 套上那条等于让模型把用户要模仿的版式拆掉。所以这里换成一条只服务本档的收口：
 * 不出额外内容、不自我复制，但**保留参考图原有的模块划分**。
 */
function copyRuleBlock(productRefCount: number, layouts: number): string[] {
  const roles: string[] = []
  if (productRefCount > 0) {
    roles.push(
      `· 第 1–${productRefCount} 张 = **产品图**：产品外观的唯一依据，颜色 / 材质 / logo / 结构 / 图案只认它`,
    )
  }
  if (layouts > 0) {
    roles.push(
      `· 第 ${productRefCount + 1}–${productRefCount + layouts} 张 = **参考版式图**：` +
        '**只取版式结构、模块顺序与信息层级，不要还原它画面里的产品**（那是别人的货）',
    )
  }
  return [
    '【版式模仿要求｜最高优先级】',
    ...(roles.length ? ['· 本次上传的图片按顺序分工如下：', ...roles] : []),
    '· 严格沿用参考版式图的版式结构、模块顺序、字号层级、留白比例与视觉节奏',
    '· 只替换产品与文案内容，**不改版式**：不增删模块，不改变模块的相对位置与占比',
    '· 参考版式图里的文字只作**篇幅参考**（几行、多长），内容一律换成下面商品与卖点',
    ...(layouts > 0
      ? []
      : ['· ⚠️ 本次**没有参考版式图** —— 按该平台通用的电商版式排版，并保持本批风格统一']),
    '· 不要在这张之外再拼贴别的内容，也不要把同一段排版重复堆两遍',
    '· 本张画面文字按上方平台的文字语言与排版规则执行',
  ]
}

/**
 * 模仿版式的**逐张**提示词 —— 一张参考版式对一张产出（2026-10-07 补）。
 *
 * ---------------------------------------------------------------------------
 * 为什么以前这里是空的（用户报障：「选了数量，提示词也没出来」）
 * ---------------------------------------------------------------------------
 * 上一版 `shapeOf('copy')` 刻意返回空解构（「它的结构来自用户上传的参考版式图，
 * 凭空造一份清单就是第二份真源」），于是 `promptsForProduct` 走
 * `buildShotPrompts` 的 `shots.map` 得到一个**空数组**，
 * 而「本批出图结构」与提示词预览都以 `prompts.length` 为准 ——
 * 界面读起来就是「这一档不出逐张提示词」。可这一档的张数是**用户自己敲的**
 * （`countOf('copy')` 取 `outputs`），他敲了 9，界面上却一份词都不给看，
 * 提交时那 9 个子任务其实都拿同一份总提示词在跑 —— 花了 9 张的钱，
 * 出 9 张逐像素一样的图，而且提交前一个字都看不到。
 *
 * 现在这份清单不是「凭空造的」：它的两项输入都是**用户真的给过的东西** ——
 *   张数（表单）、参考版式图的张数（上传物）。`copyPlanFor` 只是把两者的
 * 对应关系写出来，并把「循环 / 长图分屏」这两件事**明说给模型听**。
 */
export function buildCopyPrompts(ctx: PromptCtx, p: ProductInfo, productRefCount: number): string[] {
  const header = buildHeader(ctx, p, productRefCount)
  const layouts = Math.max(0, Math.floor(ctx.layoutRefCount) || 0)
  const plan = copyPlanFor(ctx.outputs, layouts)
  const rules = copyRuleBlock(productRefCount, layouts)

  return plan.map((slot) => [
    header,
    '',
    ...rules,
    '',
    `【本张唯一要求｜第 ${slot.no} / ${plan.length} 张】`,
    `· 版式来源：参考版式图第 ${slot.layoutNo} 张`,
    ...(layouts > 1 && slot.round > 1
      ? [
          `· 参考版式只有 ${layouts} 张、本批要 ${plan.length} 张 —— 本张是**第 ${slot.round} 轮**复用这张版式：` +
            '必须换产品角度、换场景或换文案实例，不许与前面用同一张版式的那张一模一样',
        ]
      : []),
    ...(layouts === 1 && plan.length > 1
      ? [
          `· 参考版式只有 1 张、本批要 ${plan.length} 张：把它当作**一张长图**，` +
            `本张对应图中自上而下第 ${slot.no} 段的结构与节奏；` +
            '若它只有一屏结构，则用同一版式换角度与文案实例重新演绎，**不许出两张逐像素相同的图**',
        ]
      : []),
    `· 输出比例：${ctx.ratio} —— 若参考版式比例与它不符，以 ${ctx.ratio} 为准等比重排版式`,
  ].join('\n'))
}

/**
 * 共用头部：平台规格 + 商品信息 + 参考图保真 + 合规 + 设计强度 + 跨张统一性。
 *
 * **不能**把「1. 白底正视 2. 侧面 …」这类结构清单写进来 —— 那是逐张指令的内容，
 * 混进头部会让每个子任务都以为要画全套，结果每张都出成多视角拼贴。
 *
 * 平台那几行是用户报障后补的：上一版选了平台，提示词里却只有商品名与卖点，
 * 语言 / 禁词 / 卖点排序 / 模块规格一条都没进去 —— 等于平台选择器是装饰品。
 */
export function buildHeader(ctx: PromptCtx, p: ProductInfo, refCount: number): string {
  const { profile, detail } = ctx
  const hasRef = refCount > 0
  return [
    `任务：生成 ${profile.name} 的${ctx.modeName}。`,
    '',
    `【投放平台】${profile.name}（${profile.market === 'domestic' ? '国内' : '海外'}）`,
    `· 详情页形态：${
      detail.form === 'modular'
        ? '模块化 —— 各模块尺寸不同，逐张按官方规格出，不要统一成一个比例'
        : '竖版长图 —— 宽度固定、模块纵向拼接，一屏一个主题'
    }`,
    `· ${detail.note}`,
    /*
     * 用户指定了整批统一尺寸时要**显式覆盖**上面那句「逐张按官方规格出」。
     * 不覆盖的话，提示词（尤其是 `detail.note` 里写着 970×600 / 1464×600 的那种）
     * 会让模型按官方比例画，而成图是按用户尺寸出的 —— 两边都在说尺寸，模型只会听一个。
     */
    ...(ctx.batchSize
      ? [
          `· 本批尺寸：**全部图位统一按 ${sizeLabel(ctx.batchSize)} 出图** —— 这是用户显式指定的，` +
            '覆盖上面提到的平台官方模块尺寸；画布宽高比必须严格按它，不要套用官方规格',
        ]
      : []),
    // 文件本身的硬约束（不是画面内容）—— Amazon 会因此直接拒稿，
    // 而上一版提示词里完全没有这一行：出得再好，>2MB 或 CMYK 也是白做。
    ...(detail.fileRules ? [`· 文件硬约束：${detail.fileRules}`] : []),
    // 档位必须写进提示词：同一位置两档比例不同（1.62:1 vs 2.44:1），
    // 模型不知道档位就会按训练里最常见的比例自由发挥。
    ...(detail.tier
      ? [
          `· 规格档位：${detail.tier === 'premium' ? '高级 A+（Premium）' : '基本 A+（Basic）'} —— ${
            detail.tier === 'premium'
              ? '全宽类模块官方要求单独上传移动图，两者比例不同，不允许用裁切代替'
              : '尺寸是 970 系，不要套用高级 A+ 的 1464 系比例'
          }`,
        ]
      : []),
    '',
    `【商品】${p.productName || '（未填写）'}`,
    `【卖点】${p.sellingPoints || '（未填写）'}`,
    ...sellingPointBlock(profile, p.sellingPoints),
    '',
    ...refFidelityBlock(p.productName, hasRef),
    '',
    textLanguageRule(profile),
    bannedBlock(profile),
    `· 风格关键词：${profile.styleKeywords.join('、')}`,
    '',
    '【设计强度锁】',
    '· 禁止整批塌缩成同一个模板（同色渐变背景 + 左上大标题 + 居中产品 + 小标签）',
    '· 每屏换构图尺度：远景全貌 / 中景使用 / 微距细节至少各出现一次',
    '· 场景化高密度：前中后景层次清晰，不要无意义留白，也不要堆无意义装饰英文',
    '',
    '【跨张统一要求】',
    '· 本批图片的光线、色调、镜头语言保持一致，像同一次拍摄',
    hasRef
      ? '· 全部图片里的产品必须是参考图里的同一件实物：颜色、材质、logo、图案逐张逐处一致，不得在不同张里变成另一款'
      : '· 产品外观在全部图片中完全一致',
  ].join('\n')
}

/**
 * 「AI 重写这一张」的上下文（`POST /api/aigc/prompt/refine`，`kind='image-shot'`）。
 *
 * 2026-10-08 用户口径：「可以修改提示词，还有提示词生成可以多一个 AI 按钮重置」——
 * 落在提交前的报价确认弹窗里，**每一张词旁边一颗按钮**。
 *
 * ---------------------------------------------------------------------------
 * 为什么不复用 `buildHeader`，也不把硬约束的全文抄进来
 * ---------------------------------------------------------------------------
 * ① **待改写的那一段不从这里走** —— 它是 `refinePrompt` 的 `prompt` 字段，
 *    也就是模板拼出来的那一份逐张词，硬约束（零可读文字 / 产品还原 / 不编事实）
 *    逐字都在里面，后端 system 已明令「原有硬约束一条都不许丢」。
 *    这里再抄一遍就是同一件事两个真源：模板里改了约束、这里没跟着改，
 *    两边就会对模型说不同的话，而产物看着都是一段通顺的中文，查不出来。
 * ② **`buildHeader` 带着整批的解构**（本批 N 张、跨张统一要求、设计强度锁），
 *    喂给一个「只重写其中一张」的模型，它会顺手把别的张也写出来 ——
 *    而返回的正文会**整段覆盖这一张的词**，多出来的张就是错的。
 *
 * 所以 brief 只补**原提示词里没有的那层语境**：这一张在一批里的位次、
 * 平台的禁词与图内文字语言、参考图分工。这三样正是模型最容易忽略的。
 *
 * ⚠️ `label` 与「本批出图结构」卡、确认弹窗里那一行**逐字同源**
 * （由页面用同一个 `shotLabel(i)` 算）。三处各写一遍就会各说各的，
 * 而这是「AI 到底在重写哪一张」的唯一判据。
 */
export function buildShotRefineBrief(
  ctx: PromptCtx,
  p: ProductInfo,
  refCount: number,
  label: string,
  total: number,
): string {
  const { profile, detail } = ctx
  const layoutCount = ctx.layoutRefCount
  const refLine =
    refCount + layoutCount > 0
      ? `产品图 ${refCount} 张` +
        (layoutCount
          ? ` · 参考版式图 ${layoutCount} 张（**只借版式与构图，不要照抄里面的商品**）`
          : '')
      : '无'
  return [
    '这是一张电商成品图的上下文。要你重写的是**其中一张**的提示词。',
    '',
    `【这一张的定位】${label}`,
    `【本批张数】共 ${total} 张 —— 这一张只是其中一张：不要写成一批、不要输出别的张`,
    `【产出形态】${ctx.modeName}`,
    `【投放平台】${profile.name}（${profile.market === 'domestic' ? '国内' : '海外'}）`,
    `· 平台规格：${detail.note}`,
    `· 本张比例：${ctx.ratio}`,
    '',
    `【商品】${p.productName || '（未填写）'}`,
    `【卖点】${p.sellingPoints || '（未填写）'}`,
    `【参考图】${refLine}`,
    `【风格关键词】${profile.styleKeywords.join('、')}`,
    '',
    textLanguageRule(profile),
    bannedBlock(profile),
    '',
    '【要你做的】',
    '把「当前这版提示词」改写成更贴合**这一张**的版本，硬要求：',
    '· 原提示词里的硬约束**一条都不许丢** —— 整幅画面不出现任何可读文字、' +
      '一个产品完整占据画面、产品外观严格还原、上下文里没有的事实一律不许编；',
    '· 把这一张的画面写到可拍摄：主体是什么、机位与视角、光线与材质表现、' +
      '构图与留白各写清楚，不要写「展示产品」这类谁都能填的空话；',
    '· **只输出这一张的提示词正文**：不要带图号、不要输出多份、不要复述上面的信息表。',
  ].join('\n')
}

/**
 * 主图 / 模仿版式的逐张提示词 —— 固定解构，一张一段。
 *
 * 与详情页的区别：这里每张是**独立素材**（白底/场景/卖点/细节），
 * 不带全篇叙事线；详情页那 N 屏是一篇连贯的页，必须带（见 `buildDetailPrompts`）。
 */
function buildShotPrompts(ctx: PromptCtx, p: ProductInfo, refCount: number): string[] {
  const header = buildHeader(ctx, p, refCount)
  return ctx.shots.map((shot, i) =>
    [
      header,
      '',
      `【本张唯一要求｜第 ${i + 1} / ${ctx.shots.length} 张】`,
      `· 本屏模块：${shot.name}`,
      `· 出图尺寸：${sizeLabel(shot.size)}${
        ratioHint(shot.size) ? `（宽高比 ${ratioHint(shot.size)}）` : ''
      } —— 画布宽高比必须严格按它，不要套用本批其它张的比例`,
      `· 本屏任务：${shot.task}`,
      // 移动端资产要单独声明：官方是两份独立文件，且比例与桌面差得远
      // （2.44:1 vs 4:3），不声明的话模型会照着上一张的构图缩一下交差。
      ...(shot.isMobile
        ? [
            '· 这一份是**移动端资产**：官方要求与桌面图分别上传，禁止拿桌面图裁切或拉伸得到；必须为竖屏重新构图，主体放大、要素精简',
          ]
        : []),
      shot.textFree
        ? '· 画面文字：本张画布内不得出现任何文字、水印、边框、logo（该平台合规硬约束，出现即需下架）'
        : '· 画面文字：按上方平台的文字语言与排版规则执行，标题短而具体，不要长句',
      ONE_SHOT_RULE,
    ].join('\n'),
  )
}

/**
 * **逐行提示词的唯一入口** —— 单个模式传表单那一项，批量模式传 CSV 的每一行。
 *
 * 逐屏提示词（详情页）由后端按序号分发给 N 个子任务，尺寸也按序号逐张分发；
 * 缺位时后端回退到总 prompt 与整批比例（那份也带了「严禁拼贴」，不会退化成拼贴图）。
 */
export function promptsForProduct(ctx: PromptCtx, p: ProductInfo, refCount: number): string[] {
  /*
   * 模仿版式走**自己的逐张函数**（一张版式对一张产出）。
   * 它既不属于「逐屏叙事」（那里有一套买家决策链，版式档不吃这套），
   * 也不属于下面的 `buildShotPrompts`（那里的 `shots` 对版式档是空的 ——
   * 正是这份空数组让预览恒空，见 `buildCopyPrompts` 的说明）。
   */
  if (ctx.mode === 'copy') {
    return buildCopyPrompts(ctx, p, refCount)
  }
  /*
   * 详情页（平台官方模块）走**逐屏叙事提示词**：
   * 每屏都带着全篇叙事线、前后承接、本屏的视觉角色与反面清单。
   * 这一层是「连贯」的唯一来源 —— 上一版用通用逐张模板，
   * N 份提示词除模块名以外逐字相同，出 N 张几乎一样的图。
   */
  if (ctx.plannedShots.length) {
    return buildDetailPrompts({
      header: buildHeader(ctx, p, refCount),
      shots: ctx.plannedShots,
      fallbackRatio: ctx.ratio,
    })
  }
  return buildShotPrompts(ctx, p, refCount)
}

/**
 * 「总」提示词 —— 面板里的预览，同时是「后端漏收逐屏 prompts 时」的兜底。
 *
 * 详情页那支 = 头部 + **整篇叙事线**（不含任何一屏的专属内容）；兜底那份也带着
 * 全篇规划，所以即使退化成一整批共用一份，也不会回到「六个模块名排一排」那种清单。
 *
 * ⚠️ 模仿版式那一支的规则块与逐张提示词**共用 `copyRuleBlock`** ——
 * 它是 `prompts[0] ?? fallback` 的那个「?? 后面」，
 * 两处各写一遍文案的话，兜底那份就会和用户预览的那份对不上。
 */
export function fallbackPrompt(ctx: PromptCtx, p: ProductInfo, refCount: number): string {
  const header = buildHeader(ctx, p, refCount)
  if (ctx.mode === 'copy') {
    const layouts = Math.max(0, Math.floor(ctx.layoutRefCount) || 0)
    const plan = copyPlanFor(ctx.outputs, layouts)
    return [
      header,
      '',
      ...copyRuleBlock(refCount, layouts),
      '',
      `【本批产出】共 ${plan.length} 张，逐张对应一张参考版式：`,
      ...plan.map(
        (s) =>
          `${s.no}. 沿用参考版式第 ${s.layoutNo} 张${s.round > 1 ? `（第 ${s.round} 轮复用）` : ''}`,
      ),
    ].join('\n')
  }
  if (ctx.plannedShots.length) {
    /*
     * 张数超过官方图位数时要**显式说出来**。
     *
     * 用户自己敲了 12 张、而这一档只有 8 个图位（2026-09-30 起张数是自由输入），
     * 多出来的 4 张是官方序列循环出来的。不说的话，模型看到「本批 12 屏」里
     * 有两屏都叫「主图 · 白底」，最省事的做法就是把第一张重画一遍交差 ——
     * 用户拿到 12 张里 4 张是重复的，而且每一张都照着他不知道的规则多扣了钱。
     */
    const looped = ctx.plannedShots.some((s) => (s.round ?? 1) > 1)
    return [
      header,
      '',
      `【本批逐屏】共 ${ctx.plannedShots.length} 屏，每屏只回答一个买家问题 —— 合起来是一篇连贯的购买说服页`,
      ...(looped
        ? [
            '· 张数**超过了官方图位数**：超出的部分按官方模块顺序从头循环。' +
              '同一模块第二次出现时必须换构图、换角度或换场景 —— **不许出两张一样的**',
          ]
        : []),
      ...ctx.plannedShots.map(
        (s) => `${s.no}. ${BEAT_NAME[s.beat]}｜${s.question}｜${sizeLabel(s.size)}`,
      ),
    ].join('\n')
  }
  return [
    header,
    '',
    `【本批产出】共 ${ctx.shots.length || ctx.outputs} 张，每张只表现下列中的一项，各不相同`,
    ...ctx.shots.map((s, i) => `${i + 1}. ${s.name}｜${sizeLabel(s.size)}｜${s.task}`),
    ONE_SHOT_RULE,
  ].join('\n')
}
